<template>
  <v-card class="table-alarm rounded-xl card-bg card-bd h-100 d-flex flex-column" variant="elevated" elevation="0">
    <template #title>
      <div class="d-flex justify-space-between align-center w-100">
        <span>告警列表</span>
        <div class="d-flex ga-2 align-center">
          <!-- 搜索按钮 -->
          <v-menu :close-on-content-click="false" v-model="showFilterMenu">
            <template v-slot:activator="{ props }">
              <v-btn icon="mdi-magnify" variant="text" size="small" v-bind="props"></v-btn>
            </template>
            <v-card min-width="320" class="pa-4 filter-card">
              <div class="text-subtitle-1 font-weight-bold mb-4 pl-1">告警筛选</div>
              <v-text-field v-model="filterVals.stationName" placeholder="项目名称" variant="outlined" density="compact"
                hide-details class="mb-3 custom-input" rounded></v-text-field>
              <v-select v-model="filterVals.alarmType" :items="alarmTypeOptions" item-title="label" item-value="value"
                placeholder="告警类型" variant="outlined" density="compact" hide-details multiple chips
                class="mb-3 custom-input" rounded></v-select>
              <v-text-field v-model="filterVals.devUUID" placeholder="设备UUID" variant="outlined" density="compact"
                hide-details class="mb-4 custom-input" rounded></v-text-field>
              <div class="d-flex justify-end ga-2">
                <v-btn variant="outlined" @click="resetQuery" rounded class="px-6">重置</v-btn>
                <v-btn color="#3a68ff" @click="onQueryConfirm" rounded class="px-6">确定</v-btn>
              </div>
            </v-card>
          </v-menu>

          <!-- 告警历史按钮 -->
          <v-tooltip text="告警历史">
            <template v-slot:activator="{ props }">
              <v-btn icon="mdi-history" variant="text" size="small" v-bind="props" @click="goToHistory"></v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>
    </template>

    <div class="d-flex flex-column flex-grow-1 overflow-hidden px-6 pb-4">
      <!-- 表格头部 -->
      <div class="table-header d-flex">
        <div class="table-cell" style="min-width: 300px; flex: 2;">项目名称</div>
        <div class="table-cell" style="min-width: 120px; flex: 1;">设备</div>
        <div class="table-cell" style="min-width: 140px; flex: 1;">告警类型</div>
      </div>

      <!-- 表格内容 -->
      <div class="table-body flex-grow-1 overflow-hidden">
        <template v-if="groupedList.length > 0">
          <div class="swiper-container" ref="swiperRef">
            <div v-for="(partList, pageIndex) in groupedList" :key="pageIndex" class="swiper-slide"
              v-show="currentPage === pageIndex">
              <div v-for="(item, index) in partList" :key="item.id || index" class="table-row"
                :style="{ '--row-index': index }">
                <div class="table-cell" style="min-width: 300px; flex: 2;">
                  <div class="d-flex align-center">
                    <div class="order-badge" :style="{ '--border-color': getLevelColor(item.alarmLevel) }">
                      {{ pageIndex * pageSize + index + 1 }}
                    </div>
                    <div class="ml-3">
                      <div class="station-name">{{ item.stationName || '--' }}</div>
                      <div class="text-caption text-grey">开始时间: {{ item.startTime }}</div>
                    </div>
                  </div>
                </div>
                <div class="table-cell" style="min-width: 120px; flex: 1;">
                  <div>
                    <v-tooltip :text="item.equipmentName">
                      <template v-slot:activator="{ props }">
                        <div class="equipment-name" v-bind="props">{{ item.equipmentName }}
                        </div>
                      </template>
                    </v-tooltip>
                    <v-tooltip :text="item.equipmentUuid">
                      <template v-slot:activator="{ props }">
                        <div class="text-caption text-grey uuid" v-bind="props" @click="copyUUID(item.equipmentUuid)">
                          {{ item.equipmentUuid }}
                        </div>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
                <div class="table-cell" style="min-width: 140px; flex: 1;">
                  <div class="alarm-tag" :style="{ background: getLevelColor(item.alarmLevel) }">
                    {{ item.alarmName }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="empty-state d-flex align-center justify-center h-100">
          <span class="text-grey">暂无数据</span>
        </div>
      </div>

      <!-- 分页指示器 -->
      <div v-if="groupedList.length > 1" class="pagination-controls d-flex align-center justify-center ga-2 mt-2">
        <v-btn icon="mdi-chevron-left" size="small" variant="text" @click="prevPage"
          :disabled="currentPage === 0"></v-btn>
        <div class="pagination-dots d-flex ga-1">
          <div v-for="(_, index) in groupedList" :key="index" class="pagination-dot"
            :class="{ active: currentPage === index }" @click="currentPage = index"></div>
        </div>
        <v-btn icon="mdi-chevron-right" size="small" variant="text" @click="nextPage"
          :disabled="currentPage === groupedList.length - 1"></v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { getAlarmList as fetchAlarmList } from '@/api/alarm'
import dayjs from 'dayjs'

// ... (keeping imports)
const router = useRouter()

const showFilterMenu = ref(false)
const currentPage = ref(0)
const pageSize = 10
const alarmList = ref([])

const alarmTypeOptions = [
  { value: '10001', label: '超需量告警' },
  { value: '10002', label: '非计划运行' },
  { value: '10003', label: '离线告警' },
  { value: '10004', label: '逆流告警' },
  { value: '10005', label: '温差告警' },
  { value: '10006', label: '压差告警' },
  { value: '10007', label: '数据异常' },
]

const filterVals = reactive({
  stationName: '',
  devUUID: '',
  alarmType: []
})

const groupedList = computed(() => {
  const result = []
  for (let i = 0; i < alarmList.value.length; i += pageSize) {
    result.push(alarmList.value.slice(i, i + pageSize))
  }
  return result
})

function getLevelColor(level) {
  const colors = {
    1: '#94A3B8',
    2: '#4ABE65',
    3: '#3B82F6',
    4: '#F67A3C',
    5: '#E3001F',
  }
  return colors[level] || '#6B7280'
}

function copyUUID(uuid) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(uuid)
  }
}

function resetQuery() {
  filterVals.stationName = ''
  filterVals.devUUID = ''
  filterVals.alarmType = []
}

function onQueryConfirm() {
  showFilterMenu.value = false
  fetchData()
}

function goToHistory() {
  // 设置角色为 operation，然后跳转到首页
  // operation.vue 是首页中的一个视图，通过 role 切换显示
  localStorage.setItem('home-role', 'operation')
  router.push('/index')
}

function prevPage() {
  if (currentPage.value > 0) {
    currentPage.value--
  }
}

function nextPage() {
  if (currentPage.value < groupedList.value.length - 1) {
    currentPage.value++
  }
}

async function fetchData() {
  try {
    const s = dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const e = dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss')

    const res = await fetchAlarmList({
      pageSize: 100,
      alarmClass: "OPT",
      startTime: s,
      endTime: e,
      stationName: filterVals.stationName,
      equipmentUuid: filterVals.devUUID,
      alarmCodeList: filterVals.alarmType
    })

    // web-ui 的 request 拦截器直接返回了业务数据对象
    const data = res || {}
    const list = data.list || []

    alarmList.value = list.map((item, index) => ({
      ...item,
      index
    }))
    currentPage.value = 0
  } catch (error) {
    console.error('Failed to fetch alarm list:', error)
  }
}

onMounted(() => {
  fetchData()
})

</script>

<style scoped lang="scss">
.table-alarm {
  :deep(.v-card-item) {
    padding: 24px 28px 10px 28px !important;

    .v-card-title {
      font-weight: 400 !important;
      font-size: 20px !important;
      width: 100%;
    }
  }
}

.card-bg {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
  border: 1px solid #fff;
}

.table-header {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
}

.table-cell {
  color: #6B7280;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.table-body {
  position: relative;
}

.swiper-container {
  height: 100%;
  overflow-y: auto;
}

.swiper-slide {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.table-row {
  display: flex;
  padding: 12px 16px;
  background: transparent;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  animation: rowEnter 0.4s ease forwards;
  animation-delay: calc(var(--row-index) * 50ms);
  opacity: 0;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  .table-cell {
    color: #374151;
  }
}

@keyframes rowEnter {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.order-badge {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid var(--border-color, #3B82F6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  color: var(--border-color, #3B82F6);
}

.station-name {
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

.equipment-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uuid {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #3B82F6;
  }
}

.alarm-tag {
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
}

.empty-state {
  color: #9CA3AF;
}

.pagination-controls {
  padding: 8px 0;
}

.pagination-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #D1D5DB;
  cursor: pointer;
  transition: all 0.2s ease;

  &.active {
    background: #3B82F6;
    transform: scale(1.2);
  }

  &:hover {
    background: #9CA3AF;
  }
}

.filter-card {
  border-radius: 15px !important;
}

.custom-input {

  :deep(.v-field__input),
  :deep(.v-select__selection-text) {
    font-size: 14px !important;
  }

  :deep(.v-field__outline) {
    border-radius: 16px !important;
    border: 1px solid #D4D9E5 !important;
    opacity: 1 !important;

    /* Remove default border color to rely on our border */
    --v-field-border-opacity: 0;
  }

  /* Ensure label background is white so it covers the border if we don't notch it properly, 
       or just rely on Vuetify's rounded handling */
  :deep(.v-label) {
    font-size: 14px !important;
  }
}
</style>
